'use client'

import { Client } from 'typesense'
import { type ConfigurationOptions } from 'typesense/lib/Typesense/Configuration'

const apiKey = process.env.NEXT_PUBLIC_TYPESENSE_SEARCH_API_KEY
const nodeHost = process.env.NEXT_PUBLIC_TYPESENSE_NODE_HOST

export interface TypesenseProductV5 {
  id: string
  title: string
  sku: string
  imageUrl: string
  imageAlt: string
  minPrice: number
  maxPrice: number
  minTradePrice: number
  maxTradePrice: number
  minTradeDiscount: number
  maxTradeDiscount: number
  legacyProductId: number
  breakoutOptionCount: number
  carouselVariants: {
    legacyVariantId: number
    imageUrl: string
    imageAlt: string
  }[]
  legacyProductVariantId: number
  brand: string
  availabilityOrder: number
  forceRedirectToLegacyProductId?: number
}

export interface TypesenseExpertV4 {
  id: string
  headShotImageAlt: string
  headShotImageUrl: string
  legacyExpertId: number
  profileImageAlt: string
  profileImageUrl: string
  profileName: string
}

export interface TypesenseProductVariantV1 {
  id: string
  sku: string
  title: string
  legacyProductVariantId: number
  legacyProductId: number
  status?: 'NOT_PUBLISHED' | 'PUBLISHED' | 'ARCHIVED' | 'PENDING_APPROVAL'
  brand: string
  brandSlug: string
  updatedAt: number
  createdAt: number
  price: number
  availability: 'SOLD_OUT' | 'READY_TO_SHIP' | 'MADE_TO_ORDER' | 'ENQUIRE'
  quantity: number
  tradePrice?: number
  isCounterEnabled: boolean
  quantityUnitPlural?: string
  quantityUnitSingular?: string
  minimumQuantity: number
  images: { url: string; alt?: string }[]
  width?: number
  height?: number
  length?: number
  weight?: number
  depth?: number
  aboveTheFold?: string
  customProductDescription?: string
  customProductHeadline?: string
  isEligibleForProposition65: boolean
  enquire: boolean
  accordionDetails?: string
  description?: string
  leadTimeInWeeks?: number
  accordionCare?: string
  customDimensions?: string
  proposition65?: string
  accordionShipping?: string
  'category.lvl0': string
  'category.lvl1'?: string
  'category.lvl2'?: string
  categories: string[]
  options: Record<string, string>
  askExpertCopy?: string
  askExpertLegacyId?: number
}

export const getTypesenseConfigurationOptions = ():
  | ConfigurationOptions
  | undefined => {
  if (!!apiKey && !!nodeHost) {
    return {
      nodes: [{ port: 443, protocol: 'https', host: nodeHost as never }],
      apiKey,
      cacheSearchResultsForSeconds: 2 * 60, // Cache search results from server. Defaults to 2 minutes. Set to 0 to disable caching.
    }
  }

  return undefined
}

export const getTypesenseClient = () => {
  const configuration = getTypesenseConfigurationOptions()
  if (configuration) {
    const client = new Client(configuration)

    return client
  }

  return undefined
}

export async function* productSearchIterator(client: Client, page = 1) {
  const PER_PAGE = 250

  const searchResponse = await client
    .collections<TypesenseProductV5>('products-v5')
    .documents()
    .search({
      q: '*',
      include_fields: 'title,legacyProductId',
      per_page: PER_PAGE,
      page,
    })

  yield searchResponse.hits.map((h) => h.document)

  if (
    searchResponse.hits.length + (page - 1) * PER_PAGE <
    searchResponse.found
  ) {
    yield* productSearchIterator(client, page + 1)
  }
}
